import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { Icons } from '../lib/Icons';

const HelpTermPlanningPage = () => {
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { id: 'overview', name: 'Overview', IconComponent: Icons.Calendar },
    { id: 'creating-plans', name: 'Creating Plans', IconComponent: Icons.Plus },
    { id: 'planning-structure', name: 'Planning Structure', IconComponent: Icons.Building },
    { id: 'implementation', name: 'Implementation', IconComponent: Icons.Rocket },
    { id: 'best-practices', name: 'Best Practices', IconComponent: Icons.LightBulb }
  ];

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center"><Icons.Calendar className="w-8 h-8 mr-3" /> Term Planning Help</h1>
              <p className="text-gray-600 mt-2">
                Complete guide to creating and managing academic term plans for SEN students
              </p>
            </div>
            <Link
              to="/generate-term-plan"
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Create Term Plan
            </Link>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white border-b border-gray-200 rounded-lg shadow-sm mb-8">
          <div className="px-6">
            <nav className="flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.IconComponent className="w-5 h-5 mr-2" />
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div>
          {activeTab === 'overview' && (
            <div className="px-6 space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Term Planning Overview</h2>
                <p className="text-gray-600 mb-4">
                  Term planning is essential for organizing learning objectives, allocating resources, and 
                  ensuring that student progress is systematically tracked throughout the academic term. 
                  It provides structure and direction for both staff and students.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="flex justify-center mb-2"><Icons.Target className="w-8 h-8 text-blue-600" /></div>
                    <h3 className="font-semibold text-gray-900">Learning Objectives</h3>
                    <p className="text-sm text-gray-600 mt-2">Clear goals for the term</p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="flex justify-center mb-2"><Icons.BookOpen className="w-8 h-8 text-green-600" /></div>
                    <h3 className="font-semibold text-gray-900">Resource Planning</h3>
                    <p className="text-sm text-gray-600 mt-2">Efficient use of available resources</p>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <div className="flex justify-center mb-2"><Icons.ChartBar className="w-8 h-8 text-purple-600" /></div>
                    <h3 className="font-semibold text-gray-900">Progress Tracking</h3>
                    <p className="text-sm text-gray-600 mt-2">Monitor achievement throughout term</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'creating-plans' && (
            <div className="px-6 space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Creating Term Plans</h2>
                
                <div className="space-y-6">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-blue-900 mb-2">Step-by-Step Process</h3>
                    <ol className="text-blue-800 space-y-2 ml-4">
                      <li>1. Define Term Parameters
                        <ul className="ml-6 mt-1 space-y-1">
                          <li>• Set term start and end dates</li>
                          <li>• Identify key events and holidays</li>
                          <li>• Plan assessment periods</li>
                        </ul>
                      </li>
                      <li>2. Review Student Needs
                        <ul className="ml-6 mt-1 space-y-1">
                          <li>• Assess current student progress</li>
                          <li>• Identify areas requiring focus</li>
                          <li>• Consider individual learning plans</li>
                        </ul>
                      </li>
                      <li>3. Set Learning Objectives
                        <ul className="ml-6 mt-1 space-y-1">
                          <li>• Define clear, measurable goals</li>
                          <li>• Align with curriculum frameworks</li>
                          <li>• Consider student abilities</li>
                        </ul>
                      </li>
                      <li>4. Plan Activities and Resources
                        <ul className="ml-6 mt-1 space-y-1">
                          <li>• Design engaging activities</li>
                          <li>• Allocate necessary resources</li>
                          <li>• Plan for differentiation</li>
                        </ul>
                      </li>
                      <li>5. Create Timeline
                        <ul className="ml-6 mt-1 space-y-1">
                          <li>• Schedule key activities</li>
                          <li>• Plan progress reviews</li>
                          <li>• Include flexibility for adjustments</li>
                        </ul>
                      </li>
                    </ol>
                  </div>

                  <div className="bg-green-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-green-900 mb-2">Key Components</h3>
                    <ul className="text-green-800 space-y-1">
                      <li>• Term overview and objectives</li>
                      <li>• Weekly or monthly breakdowns</li>
                      <li>• Assessment and review schedules</li>
                      <li>• Resource requirements and allocation</li>
                      <li>• Staff responsibilities and coordination</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'planning-structure' && (
            <div className="px-6 space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Planning Structure</h2>
                
                <div className="space-y-6">
                  <div className="border-l-4 border-blue-500 pl-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center"><Icons.ClipboardDocumentList className="w-5 h-5 mr-2" /> Term Overview</h3>
                    <p className="text-gray-600 mb-2">
                      High-level summary of what will be achieved during the term.
                    </p>
                    <ul className="text-gray-600 space-y-1">
                      <li>• Overall learning objectives</li>
                      <li>• Key themes and topics</li>
                      <li>• Expected outcomes</li>
                      <li>• Success criteria</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-green-500 pl-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center"><Icons.Calendar className="w-5 h-5 mr-2" /> Weekly Breakdown</h3>
                    <p className="text-gray-600 mb-2">
                      Detailed weekly plans with specific activities and objectives.
                    </p>
                    <ul className="text-gray-600 space-y-1">
                      <li>• Weekly learning goals</li>
                      <li>• Daily activities and tasks</li>
                      <li>• Assessment opportunities</li>
                      <li>• Resource requirements</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-purple-500 pl-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center"><Icons.Target className="w-5 h-5 mr-2" /> Learning Objectives</h3>
                    <p className="text-gray-600 mb-2">
                      Specific, measurable goals for student achievement.
                    </p>
                    <ul className="text-gray-600 space-y-1">
                      <li>• Subject-specific objectives</li>
                      <li>• Skills development targets</li>
                      <li>• Knowledge acquisition goals</li>
                      <li>• Personal development aims</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-orange-500 pl-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center"><Icons.ChartBar className="w-5 h-5 mr-2" /> Assessment Planning</h3>
                    <p className="text-gray-600 mb-2">
                      Systematic approach to monitoring and evaluating progress.
                    </p>
                    <ul className="text-gray-600 space-y-1">
                      <li>• Formative assessment strategies</li>
                      <li>• Summative assessment points</li>
                      <li>• Progress review schedules</li>
                      <li>• Feedback mechanisms</li>
                    </ul>
                  </div>

                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-yellow-900 mb-2 flex items-center"><Icons.BookOpen className="w-5 h-5 mr-2" /> Resource Planning</h3>
                    <ul className="text-yellow-800 space-y-1">
                      <li>• Materials and equipment needed</li>
                      <li>• Staff time and expertise</li>
                      <li>• External resources and visits</li>
                      <li>• Budget considerations</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'implementation' && (
            <div className="px-6 space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Implementation</h2>
                
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h3 className="text-lg font-semibold text-blue-900 mb-2 flex items-center"><Icons.Rocket className="w-5 h-5 mr-2" /> Getting Started</h3>
                      <ul className="text-blue-800 space-y-1">
                        <li>• Communicate plan to all staff</li>
                        <li>• Prepare necessary resources</li>
                        <li>• Set up monitoring systems</li>
                        <li>• Begin with first week's activities</li>
                      </ul>
                    </div>

                    <div className="bg-green-50 p-4 rounded-lg">
                      <h3 className="text-lg font-semibold text-green-900 mb-2 flex items-center"><Icons.ArrowTrendingUp className="w-5 h-5 mr-2" /> During Implementation</h3>
                      <ul className="text-green-800 space-y-1">
                        <li>• Monitor progress regularly</li>
                        <li>• Adjust plans as needed</li>
                        <li>• Provide ongoing support</li>
                        <li>• Celebrate achievements</li>
                      </ul>
                    </div>
                  </div>

                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-purple-900 mb-2 flex items-center"><Icons.ArrowPath className="w-5 h-5 mr-2" /> Monitoring and Adjustment</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="font-medium text-purple-900 mb-2">Regular Reviews</h4>
                        <ul className="text-purple-800 text-sm space-y-1">
                          <li>• Weekly progress checks</li>
                          <li>• Monthly comprehensive reviews</li>
                          <li>• Mid-term assessments</li>
                          <li>• End-of-term evaluations</li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-medium text-purple-900 mb-2">Flexibility</h4>
                        <ul className="text-purple-800 text-sm space-y-1">
                          <li>• Adapt to student needs</li>
                          <li>• Modify activities if necessary</li>
                          <li>• Adjust timelines as required</li>
                          <li>• Incorporate new opportunities</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="bg-orange-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-orange-900 mb-2 flex items-center"><Icons.ChartBar className="w-5 h-5 mr-2" /> Success Indicators</h3>
                    <ul className="text-orange-800 space-y-1">
                      <li>• Student engagement and participation</li>
                      <li>• Achievement of learning objectives</li>
                      <li>• Progress in key areas</li>
                      <li>• Staff and student satisfaction</li>
                      <li>• Resource utilization efficiency</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'best-practices' && (
            <div className="px-6 space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Best Practices</h2>
                
                <div className="space-y-6">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-blue-900 mb-2 flex items-center"><Icons.ClipboardDocumentList className="w-5 h-5 mr-2" /> Planning Principles</h3>
                    <ul className="text-blue-800 space-y-1">
                      <li>• Start with clear, achievable objectives</li>
                      <li>• Consider individual student needs</li>
                      <li>• Build in flexibility for adjustments</li>
                      <li>• Ensure realistic timelines</li>
                      <li>• Plan for differentiation and support</li>
                    </ul>
                  </div>

                  <div className="bg-green-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-green-900 mb-2 flex items-center"><Icons.Handshake className="w-5 h-5 mr-2" /> Collaboration</h3>
                    <ul className="text-green-800 space-y-1">
                      <li>• Involve all relevant staff members</li>
                      <li>• Consult with specialists and therapists</li>
                      <li>• Communicate with parents/guardians</li>
                      <li>• Coordinate with external agencies</li>
                      <li>• Share best practices across teams</li>
                    </ul>
                  </div>

                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-purple-900 mb-2 flex items-center"><Icons.ChartBar className="w-5 h-5 mr-2" /> Assessment Integration</h3>
                    <ul className="text-purple-800 space-y-1">
                      <li>• Integrate assessment throughout the term</li>
                      <li>• Use multiple assessment methods</li>
                      <li>• Provide regular feedback to students</li>
                      <li>• Document progress systematically</li>
                      <li>• Use assessment data to inform planning</li>
                    </ul>
                  </div>

                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-yellow-900 mb-2 flex items-center"><Icons.ArrowPath className="w-5 h-5 mr-2" /> Continuous Improvement</h3>
                    <ul className="text-yellow-800 space-y-1">
                      <li>• Reflect on what works well</li>
                      <li>• Identify areas for improvement</li>
                      <li>• Gather feedback from all stakeholders</li>
                      <li>• Update plans based on experience</li>
                      <li>• Share lessons learned with colleagues</li>
                    </ul>
                  </div>

                  <div className="mt-8 bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2 flex items-center"><Icons.LightBulb className="w-5 h-5 mr-2" /> Pro Tips:</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Use templates to streamline planning</li>
                      <li>• Set up regular planning meetings</li>
                      <li>• Keep plans visible and accessible</li>
                      <li>• Plan for unexpected events and disruptions</li>
                      <li>• Celebrate successful term completions</li>
                      <li>• Use technology to enhance planning efficiency</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default HelpTermPlanningPage; 